import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';
import { TemplateProposedAction, ProposedActionStatus } from '../chat/types';

type TemplateProposalResponse = {
  id: string;
  operation: 'CREATE' | 'UPDATE' | 'DELETE';
  templateId: string | null;
  templateName: string;
  templateType: 'MEETING_NOTE' | 'MEETING_AGENDA';
  summary: string;
  status: ProposedActionStatus;
  changeStats: TemplateProposedAction['changeStats'];
  failureReason: string | null;
  appliedTemplateId: string | null;
};

export function toProposedAction(proposal: TemplateProposalResponse): TemplateProposedAction {
  return {
    kind: 'TEMPLATE',
    proposalId: proposal.id,
    operation: proposal.operation,
    templateId: proposal.templateId,
    templateName: proposal.templateName,
    templateType: proposal.templateType,
    summary: proposal.summary,
    changeStats: proposal.changeStats,
    status: proposal.status,
    failureReason: proposal.failureReason,
  };
}

export function usePendingTemplateProposals(conversationId: string | undefined, enabled: boolean) {
  const { data, isError } = useQuery<TemplateProposalResponse[]>({
    queryKey: ['conversations', conversationId, 'template-proposals', { status: 'PENDING' }],
    enabled: enabled && !!conversationId,
    // Admin-gating means non-admins get a 403/404; never retry or surface it as an error state.
    retry: false,
  });
  return { pendingProposals: isError ? [] : (data ?? []) };
}

export function useTemplateProposalDecision(conversationId: string | undefined) {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation<
    { data: TemplateProposalResponse },
    Error,
    { proposalId: string; decision: 'APPROVE' | 'DENY' }
  >({
    mutationFn: ({ proposalId, decision }) =>
      api.post<TemplateProposalResponse>(
        `conversations/${conversationId}/template-proposals/${proposalId}/decision`,
        { decision },
      ),
    onSuccess: response => {
      queryClient.invalidateQueries({
        queryKey: ['conversations', conversationId, 'template-proposals'],
      });
      if (response.data.status === 'APPLIED') {
        // The approved change created/updated/deleted a template server-side; drop every cached
        // template list and detail so already-mounted views (templates page, secret menu)
        // re-fetch the new structure instead of showing the stale cache.
        queryClient.invalidateQueries({
          predicate: query =>
            query.queryKey.includes('data-export-templates') ||
            query.queryKey.includes('templates'),
        });
      }
    },
  });
  return { decideProposal: mutateAsync, isDeciding: isPending };
}
